/*
 * Copyright (c) 2023 Irian Software Development SRL. All Rights Reserved.
 * This software is the proprietary information of Irian Software Development SRL.
 * Use is subject to license and non-disclosure terms
 */

import { EventEmitter } from '@angular/core';
import { map } from 'rxjs/operators';
import * as _ from 'lodash';
import { Table } from 'primeng/table';
import { UiStateHistoryService } from '@irian-ro/ui-state-history';

export abstract class PrimengTableHelper {
  protected uiStateHistoryService: UiStateHistoryService | undefined = undefined;
  protected historyState: {
    filters?: any;
    first?: any;
    sortField?: any;
    sortOrder?: any;
  } = { filters: {} };
  private staticLazyEvent: any = null;

  protected constructor(uiStateHistoryService: UiStateHistoryService) {
    this.uiStateHistoryService = uiStateHistoryService;

    this.lazyLoadEventEmitter = new EventEmitter();

    this.registerLazyLoadEvent();
  }

  protected _table!: Table;

  get table() {
    return this._table;
  }

  set table(table: Table) {
    this._table = table;
    this.historyState = this.uiStateHistoryService?.getUrlStateHistory() ?? { filters: {} };

    table.filters = this.historyState.filters ? this.historyState.filters : { filters: {} };
    table.first = this.historyState.first ? this.historyState.first : 0;
    table.sortField = this.historyState.sortField ? this.historyState.sortField : null;
    table.sortOrder = this.historyState.sortOrder ? this.historyState.sortOrder : null;

    this.setTableCallBack();

    const createOriginalLazyLoadMetadata = Object.getPrototypeOf(table).createLazyLoadMetadata.bind(table);

    table.createLazyLoadMetadata = () => {
      return this.updateLazyEventPagination(createOriginalLazyLoadMetadata());
    };

    table.onLazyLoad.emit(table.createLazyLoadMetadata());

    table.onLazyLoad.subscribe((event) => {
      this.emitLazyLoadEvent(event);
    });
  }

  private _lazyLoadEventEmitter!: EventEmitter<any>;

  protected get lazyLoadEventEmitter(): EventEmitter<any> {
    return this._lazyLoadEventEmitter;
  }

  protected set lazyLoadEventEmitter(value: EventEmitter<any>) {
    this._lazyLoadEventEmitter = <EventEmitter<any>>value.pipe(
      map((event: any) => {
        this.staticLazyEvent = _.cloneDeep(event);

        return event;
      })
    );
  }

  emitLazyLoadEvent(event: any) {
    this.lazyLoadEventEmitter.next({ ...event });
  }

  clearTableFilters() {
    this._table.filters = {};
    this._table.onLazyLoad.emit(this._table.createLazyLoadMetadata());
  }

  protected setTableCallBack() {
    return undefined;
  }

  protected updateLazyEventPagination(event: any) {
    if (this.staticLazyEvent) {
      if (!_.isEqual(event.filters, this.staticLazyEvent?.filters)) {
        event.first = 0;
      }

      if ( this.staticLazyEvent && event.sortField !== this.staticLazyEvent.sortField || event.sortOrder !== this.staticLazyEvent.sortOrder) {
        event.first = 0;
      }
    }

    return { ...event };
  }

  private registerLazyLoadEvent() {
    this.uiStateHistoryService?.addObservables({
      value: this._lazyLoadEventEmitter,
      options: { storeLocation: ['url'] },
    });
  }
}
